import type {
  GuildWarAbsenceRequestedFor,
  GuildWarCandidateSource,
  GuildWarWeeklyAvailability,
  MartialArtPath,
  SecondaryPathFilterValue,
} from "@/core/guild-war/types"

import { GUILD_WAR_PATH_REGISTRY } from "@/core/guild-war/path-registry"
import { toGuildWarPathDisplayLabel } from "@/lib/guild-war-path-display"

type AbsenceState = "none" | "pending" | "approved"
type CandidatePoolSortBy =
  | "member"
  | "availability"
  | "primaryPath"
  | "secondaryPath"
  | "source"
  | "absenceState"
type CandidatePoolSortOrder = "asc" | "desc"
type ManualParticipantMode = "weekly" | "permanent"

type LabeledSelectOption<TValue extends string> = {
  value: TValue
  label: string
}

export type CandidatePoolFilters = {
  weekId: string
  day: "all" | GuildWarAbsenceRequestedFor
  source: GuildWarCandidateSource
  paths: MartialArtPath[]
  secondaryPath: SecondaryPathFilterValue
  absenceState: "all" | AbsenceState
  sortBy: CandidatePoolSortBy
  sortOrder: CandidatePoolSortOrder
}

const MANUAL_MODE_LABELS: Record<ManualParticipantMode, string> = {
  weekly: "Weekly signup",
  permanent: "Permanent member",
}

const WEEKLY_AVAILABILITY_LABELS: Record<GuildWarWeeklyAvailability, string> = {
  saturday: "Saturday",
  sunday: "Sunday",
  both: "Both days",
}

const DAY_FILTER_LABELS: Record<CandidatePoolFilters["day"], string> = {
  all: "All",
  saturday: "Saturday",
  sunday: "Sunday",
  both: "Both",
}

const SOURCE_FILTER_LABELS: Record<CandidatePoolFilters["source"], string> = {
  both: "Both",
  permanent: "Permanent",
  weekly: "Weekly",
}

const ABSENCE_FILTER_LABELS: Record<
  CandidatePoolFilters["absenceState"],
  string
> = {
  all: "All",
  none: "None",
  pending: "Pending",
  approved: "Active",
}

const SORT_BY_LABELS: Record<CandidatePoolSortBy, string> = {
  member: "Member",
  availability: "Availability",
  primaryPath: "Primary Skill Path",
  secondaryPath: "Secondary Skill Path",
  source: "Source",
  absenceState: "Absence",
}

const SORT_ORDER_LABELS: Record<CandidatePoolSortOrder, string> = {
  asc: "Ascending",
  desc: "Descending",
}

export const defaultCandidatePoolFilters: CandidatePoolFilters = {
  weekId: "",
  day: "all",
  source: "both",
  paths: [],
  secondaryPath: "all",
  absenceState: "all",
  sortBy: "member",
  sortOrder: "asc",
}

export const MANUAL_MODE_OPTIONS = Object.entries(MANUAL_MODE_LABELS).map(
  ([value, label]) => ({ value, label }),
) as LabeledSelectOption<ManualParticipantMode>[]

export const WEEKLY_AVAILABILITY_OPTIONS = Object.entries(
  WEEKLY_AVAILABILITY_LABELS,
).map(([value, label]) => ({
  value,
  label,
})) as LabeledSelectOption<GuildWarWeeklyAvailability>[]

export const DAY_FILTER_OPTIONS = Object.entries(DAY_FILTER_LABELS).map(
  ([value, label]) => ({ value, label }),
) as LabeledSelectOption<CandidatePoolFilters["day"]>[]

export const SOURCE_FILTER_OPTIONS = Object.entries(SOURCE_FILTER_LABELS).map(
  ([value, label]) => ({ value, label }),
) as LabeledSelectOption<CandidatePoolFilters["source"]>[]

export const ABSENCE_FILTER_OPTIONS = Object.entries(ABSENCE_FILTER_LABELS).map(
  ([value, label]) => ({ value, label }),
) as LabeledSelectOption<CandidatePoolFilters["absenceState"]>[]

export const SORT_BY_OPTIONS = Object.entries(SORT_BY_LABELS).map(
  ([value, label]) => ({
    value,
    label,
  }),
) as LabeledSelectOption<CandidatePoolSortBy>[]

export const SORT_ORDER_OPTIONS = Object.entries(SORT_ORDER_LABELS).map(
  ([value, label]) => ({ value, label }),
) as LabeledSelectOption<CandidatePoolSortOrder>[]

export const MANUAL_PRIMARY_PATH_OPTIONS = GUILD_WAR_PATH_REGISTRY.map(
  (path) => ({
    value: path.key,
    label: toGuildWarPathDisplayLabel(path.key, {
      variant: "full",
      includeEmoji: true,
      fallback: path.key,
    }),
  }),
) as LabeledSelectOption<MartialArtPath>[]

export const MANUAL_SECONDARY_PATH_OPTIONS = [
  { value: "none", label: "None" },
  ...GUILD_WAR_PATH_REGISTRY.map((path) => ({
    value: path.key,
    label: toGuildWarPathDisplayLabel(path.key, {
      variant: "full",
      includeEmoji: true,
      fallback: path.key,
    }),
  })),
] as LabeledSelectOption<"none" | MartialArtPath>[]

export const PRIMARY_PATH_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  ...GUILD_WAR_PATH_REGISTRY.map((path) => ({
    value: path.key,
    label: toGuildWarPathDisplayLabel(path.key, {
      variant: "short",
      fallback: path.key,
    }),
  })),
] as LabeledSelectOption<"all" | MartialArtPath>[]

export const SECONDARY_PATH_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "none", label: "None" },
  ...GUILD_WAR_PATH_REGISTRY.map((path) => ({
    value: path.key,
    label: toGuildWarPathDisplayLabel(path.key, {
      variant: "short",
      fallback: path.key,
    }),
  })),
] as LabeledSelectOption<SecondaryPathFilterValue>[]
